// 12's settings registration — the FIRST registrant of module 22's host seam, and the thing
// ORP-18893 precondition 5 is waiting on ("a registered stub from one other module — the seam is
// unproven until a pane 22 does not import renders inside SettingsLayout").
//
// Why a function and not a module side effect, same reason as 22's own `install.ts`:
// `resetSettingsRegistry()` clears nav entries wholesale, so an entry registered at module-eval time
// survives exactly until the first spec that resets — and then silently vanishes for the rest of
// that file. A registration a test can re-run is the only shape that survives.
//
// The registrar arrives as an ARGUMENT (contract 22 §8). 12 never imports 22's registry and 22 never
// imports 12; `app/bootstrap.ts` is the only place that knows both exist (ledger §A11/§A14).
import type { RouteRecordRaw, RouteRecordSingleView } from 'vue-router'

import { useIntegrationsRbac } from './composables/useIntegrationsRbac'
import { createIntegrationSettingsRoutes } from './routes'
import { CONNECTIONS_ROUTE } from './routeTarget'

import type { SettingsNavEntry } from '@/features/workspace-settings'

/** The two register functions the composition root hands every settings registrant (contract 22 §8). */
export interface IntegrationsSettingsRegistrar {
  registerNav: (entry: SettingsNavEntry) => void
  registerRoutes: (records: RouteRecordRaw[]) => void
}

export interface IntegrationsSettingsOptions {
  /** App-owned composition host; module 12 remains independent from follow-up feature panes. */
  connectionsComponent?: RouteRecordSingleView['component']
}

/**
 * Register 12's Connections pane into module 22's settings surface.
 *
 * Routes go first: `registerSettingsChildRoutes` throws once the router is sealed, so a mis-ordered
 * composition root should fail on the route — the loud half — rather than leave a nav entry
 * pointing at a name that never resolves.
 */
export function installIntegrationsSettingsNav(
  reg: IntegrationsSettingsRegistrar,
  options: IntegrationsSettingsOptions = {},
): void {
  reg.registerRoutes(createIntegrationSettingsRoutes(options.connectionsComponent))

  reg.registerNav({
    key: 'connections',
    // 12's OWN i18n namespace — contract 22 §5: 22 renders an entry's label and never owns its copy.
    labelKey: 'integrations.nav.connections',
    // The typed local icon catalog now owns the network silhouette used by the accepted settings
    // reference. Keeping it on this registry entry lets every settings host render the same glyph
    // without a route-specific asset branch.
    icon: 'circle-nodes',
    // Connections is a full settings destination after Profile, matching New Front's root order.
    group: 'connections',
    order: 10,
    routeName: CONNECTIONS_ROUTE.name,
    visible: () => useIntegrationsRbac().canViewConnections.value,
  })
}
